import React, { createContext, useContext, useEffect, useState } from "react";
import { fetch_session } from "../controllers/loginRoutes.js";
import {
  AUTH_TOKEN_KEY,
  clearPortalSessionStorage,
  readConfirmedAuthSnapshot,
  readActivePortalMode,
  readAuthToken,
  writeConfirmedAuthSnapshot,
  writeActivePortalMode,
} from "../utils/parentPortalAuth.js";
import {
  normalizeManagementRoleKey,
  normalizeManagementRoleValue,
  normalizeSystemRoleValue,
  resolveSessionRoles,
} from "../utils/rbac.js";
import { recordClientDiagnostic } from "../utils/clientDiagnostics.js";

const AuthContext = createContext({
  loading: true,
  me: null,
  user: null,
  school: null,
  systemRole: "",
  managementRole: "",
  activePortal: "",
  availablePortals: [],
  managementPortalRole: "",
  schoolId: "",
  divisionScope: [],
  classScope: [],
  permissions: {},
  rolePermissions: null,
  scope: null,
  token: "",
  tokenExpiresAt: "",
  connectionStatus: "online",
  connectionMessage: "",
  sessionUnavailable: false,
  lastSessionConfirmedAt: null,
  refresh: async () => {},
  switchPortal: async () => null,
  logout: () => {},
});

const TOKEN_STORAGE_KEYS = new Set([
  AUTH_TOKEN_KEY,
  "token",
  "auth",
  "jwt",
  "sessionToken",
  "access_token",
  "id_token",
]);

const normalizeScopeList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return [];
    if (raw.startsWith("[")) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => String(item || "").trim())
            .filter(Boolean);
        }
      } catch (error) {
        // ignore
      }
    }
    return raw
      .split(",")
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }
  return [];
};

const buildAuthState = (session) => {
  const { systemRole, managementRole, managementRoleRaw, user } =
    resolveSessionRoles(session);
  const rbac =
    session?.rbac && typeof session.rbac === "object" ? session.rbac : {};
  const normalizedSystem = normalizeSystemRoleValue(systemRole);
  const normalizedManagementValue = normalizeManagementRoleValue(
    managementRole || managementRoleRaw,
  );
  const normalizedManagementKey = normalizeManagementRoleKey(
    normalizedManagementValue,
  );

  const schoolFromSession =
    session?.school && typeof session.school === "object" ? session.school : {};

  const schoolId = schoolFromSession.id || rbac.school_id || "";
  const schoolCode = schoolFromSession.code || "";
  const schoolName = schoolFromSession.name || "";
  const hasSections =
    schoolFromSession.has_sections ??
    schoolFromSession.hasSections ??
    undefined;
  const setupStatus = schoolFromSession.setup_status || null;
  const moduleAccess =
    schoolFromSession.module_access || schoolFromSession.moduleAccess || null;

  const school =
    schoolId ||
    schoolCode ||
    schoolName ||
    hasSections !== undefined ||
    setupStatus ||
    moduleAccess
      ? {
          id: schoolId || null,
          code: schoolCode || null,
          name: schoolName || null,
          has_sections:
            typeof hasSections === "boolean" ? hasSections : undefined,
          setup_status: setupStatus || undefined,
          module_access: moduleAccess || undefined,
          moduleAccess: moduleAccess || undefined,
        }
      : null;

  const divisionScope = normalizeScopeList(rbac.division_scope);
  const classScope = normalizeScopeList(rbac.class_scope);
  const rawPermissions = rbac.permissions || {};
  const rolePermissions =
    session?.rolePermissions && typeof session.rolePermissions === "object"
      ? session.rolePermissions
      : null;
  const sessionPermissions =
    session?.permissions && typeof session.permissions === "object"
      ? session.permissions
      : {};
  const normalizedPermissions = {
    allowedModules: Array.isArray(sessionPermissions.allowedModules)
      ? sessionPermissions.allowedModules
      : [],
    allowedActions: Array.isArray(sessionPermissions.allowedActions)
      ? sessionPermissions.allowedActions
      : [],
  };
  const scope =
    session?.scope && typeof session.scope === "object" ? session.scope : null;
  const portalEnvelope =
    session?.portal && typeof session.portal === "object" ? session.portal : {};
  const availablePortals = Array.from(
    new Set(
      (Array.isArray(portalEnvelope.available) ? portalEnvelope.available : [])
        .map((value) =>
          String(value || "")
            .trim()
            .toLowerCase(),
        )
        .filter((value) => value === "teacher" || value === "management"),
    ),
  );
  const activePortal = String(portalEnvelope.active || "")
    .trim()
    .toLowerCase();
  const managementPortalRole = normalizeManagementRoleKey(
    portalEnvelope.managementRole || "",
  );

  const firstName = user?.first_name || user?.firstName || null;
  const lastName = user?.last_name || user?.lastName || null;
  const displayName =
    user?.displayName ||
    [firstName, lastName].filter(Boolean).join(" ").trim() ||
    user?.name ||
    user?.username ||
    user?.userId ||
    user?.email ||
    "";

  const me =
    user && typeof user === "object"
      ? {
          ...user,
          system_role: normalizedSystem || null,
          systemRole: normalizedSystem || null,
          management_role:
            normalizedSystem === "ADMIN"
              ? normalizedManagementValue || null
              : null,
          managementRole:
            normalizedSystem === "ADMIN"
              ? normalizedManagementValue || null
              : null,
          school_id: schoolId || null,
          schoolId: schoolId || null,
          school,
          division_scope: divisionScope,
          class_scope: classScope,
          scopeClassIds: classScope,
          permissions: rawPermissions,
          rolePermissions,
          scope,
          firstName,
          lastName,
          displayName,
          activePortal,
          availablePortals,
          managementPortalRole,
        }
      : null;

  return {
    me,
    user: me,
    school,
    systemRole: normalizedSystem || "",
    managementRole: normalizedManagementKey || "",
    activePortal,
    availablePortals,
    managementPortalRole,
    schoolId: schoolId || "",
    divisionScope,
    classScope,
    permissions: normalizedPermissions,
    rolePermissions,
    scope,
  };
};

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState(() => ({
    loading: true,
    me: null,
    user: null,
    school: null,
    systemRole: "",
    managementRole: "",
    activePortal: "",
    availablePortals: [],
    managementPortalRole: "",
    schoolId: "",
    divisionScope: [],
    classScope: [],
    permissions: {},
    rolePermissions: null,
    scope: null,
    token: "",
    tokenExpiresAt: "",
    connectionStatus: "online",
    connectionMessage: "",
    sessionUnavailable: false,
    lastSessionConfirmedAt: null,
  }));

  const hydrate = async (options = {}) => {
    const token = readAuthToken();
    if (!token) {
      setState((prev) => ({
        ...prev,
        loading: false,
        me: null,
        user: null,
        school: null,
        systemRole: "",
        managementRole: "",
        activePortal: "",
        availablePortals: [],
        managementPortalRole: "",
        schoolId: "",
        divisionScope: [],
        classScope: [],
        permissions: {},
        rolePermissions: null,
        scope: null,
        token: "",
        tokenExpiresAt: "",
        connectionStatus: "online",
        connectionMessage: "",
        sessionUnavailable: false,
        lastSessionConfirmedAt: null,
      }));
      return null;
    }
    try {
      const session = await fetch_session({
        forceRefresh: Boolean(options.force),
      });
      const status = Number(session?._httpStatus || 0);
      if (status === 401 || status === 403) {
        if (status === 403 && options.preserveOnForbidden) {
          setState((prev) => ({ ...prev, loading: false }));
          return null;
        }
        clearPortalSessionStorage();
        setState((prev) => ({
          ...prev,
          loading: false,
          me: null,
          user: null,
          school: null,
          systemRole: "",
          managementRole: "",
          activePortal: "",
          availablePortals: [],
          managementPortalRole: "",
          schoolId: "",
          divisionScope: [],
          classScope: [],
          permissions: {},
          rolePermissions: null,
          scope: null,
          token: "",
          tokenExpiresAt: "",
          connectionStatus: "online",
          connectionMessage: "",
          sessionUnavailable: false,
          lastSessionConfirmedAt: null,
        }));
        return null;
      }
      const next = buildAuthState(session);
      const tokenValue = readAuthToken();
      const tokenExpiresAt =
        session?.expiresAt || session?.tokenExpiresAt || "";
      const confirmedAt = new Date();
      setState((prev) => ({
        ...prev,
        ...next,
        token: tokenValue,
        tokenExpiresAt,
        loading: false,
        connectionStatus: ["offline", "error", "recovering"].includes(
          prev.connectionStatus,
        )
          ? "back-online"
          : "online",
        connectionMessage: "",
        sessionUnavailable: false,
        lastSessionConfirmedAt: confirmedAt,
      }));
      writeConfirmedAuthSnapshot({
        ...next,
        tokenExpiresAt,
      });
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("erp-connection-recovered"));
      }
      return next;
    } catch (error) {
      const snapshot = readConfirmedAuthSnapshot();
      const offline =
        typeof navigator !== "undefined" && navigator.onLine === false;
      recordClientDiagnostic("auth_bootstrap_failed", {
        operation: "authentication",
        authOutcome: "bootstrap_failed",
        online: !offline,
        failureCategory: offline ? "network_unavailable" : "unknown_failure",
      });
      setState((prev) => {
        const fallback = prev.me
          ? prev
          : snapshot?.state
            ? {
                ...prev,
                ...snapshot.state,
                token: readAuthToken(),
                lastSessionConfirmedAt: snapshot.confirmedAt
                  ? new Date(snapshot.confirmedAt)
                  : null,
              }
            : prev;
        return {
          ...fallback,
          loading: false,
          connectionStatus: offline ? "offline" : "error",
          connectionMessage: offline
            ? "You're offline. Showing the last information loaded."
            : "Connection problem. Showing the last information loaded.",
          sessionUnavailable: !fallback.me,
        };
      });
      return null;
    }
  };

  useEffect(() => {
    hydrate();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__erp_auth_ready = !state.loading;
  }, [state.loading]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const markOffline = () =>
      setState((prev) => ({
        ...prev,
        connectionStatus: "offline",
        connectionMessage:
          "You're offline. Showing the last information loaded.",
        sessionUnavailable: !prev.me,
      }));
    const markRequestFailure = () =>
      setState((prev) => ({
        ...prev,
        connectionStatus:
          typeof navigator !== "undefined" && navigator.onLine === false
            ? "offline"
            : "error",
        connectionMessage:
          typeof navigator !== "undefined" && navigator.onLine === false
            ? "You're offline. Showing the last information loaded."
            : "Connection problem. Showing the last information loaded.",
        sessionUnavailable: !prev.me,
      }));
    const recover = () => {
      if (!readAuthToken()) return;
      setState((prev) => ({ ...prev, connectionStatus: "recovering" }));
      hydrate({ force: true, recovery: true });
    };
    window.addEventListener("offline", markOffline);
    window.addEventListener("online", recover);
    window.addEventListener("erp-network-error", markRequestFailure);
    return () => {
      window.removeEventListener("offline", markOffline);
      window.removeEventListener("online", recover);
      window.removeEventListener("erp-network-error", markRequestFailure);
    };
  }, []);

  useEffect(() => {
    if (state.connectionStatus !== "back-online") return undefined;
    recordClientDiagnostic("offline_recovered", {
      operation: "authentication",
      recovered: true,
      online: true,
    });
    const timer = window.setTimeout(
      () =>
        setState((prev) => ({
          ...prev,
          connectionStatus: "online",
          connectionMessage: "",
        })),
      3000,
    );
    return () => window.clearTimeout(timer);
  }, [state.connectionStatus]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleTokenChange = () => {
      const token = readAuthToken();
      if (!token) {
        setState({
          loading: false,
          me: null,
          user: null,
          school: null,
          systemRole: "",
          managementRole: "",
          schoolId: "",
          divisionScope: [],
          classScope: [],
          permissions: {},
          rolePermissions: null,
          scope: null,
          token: "",
          tokenExpiresAt: "",
          connectionStatus: "online",
          connectionMessage: "",
          sessionUnavailable: false,
          lastSessionConfirmedAt: null,
        });
        return;
      }
      setState((prev) => ({ ...prev, loading: true }));
      hydrate({ force: true });
    };

    const handleStorage = (event) => {
      if (!event?.key) return;
      if (TOKEN_STORAGE_KEYS.has(event.key)) {
        handleTokenChange();
      }
    };

    window.addEventListener("auth-token-changed", handleTokenChange);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("auth-token-changed", handleTokenChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const logout = () => {
    clearPortalSessionStorage();
    setState({
      loading: false,
      me: null,
      user: null,
      school: null,
      systemRole: "",
      managementRole: "",
      activePortal: "",
      availablePortals: [],
      managementPortalRole: "",
      schoolId: "",
      divisionScope: [],
      classScope: [],
      permissions: {},
      rolePermissions: null,
      scope: null,
      token: "",
      tokenExpiresAt: "",
      connectionStatus: "online",
      connectionMessage: "",
      sessionUnavailable: false,
      lastSessionConfirmedAt: null,
    });
  };

  const switchPortal = async (mode) => {
    const requested = String(mode || "")
      .trim()
      .toLowerCase();
    if (!state.availablePortals.includes(requested)) {
      throw new Error("That portal is not assigned to this account.");
    }
    const previous = readActivePortalMode();
    writeActivePortalMode(requested);
    setState((current) => ({ ...current, loading: true }));
    const next = await hydrate({ force: true, preserveOnForbidden: true });
    if (!next || next.activePortal !== requested) {
      writeActivePortalMode(previous);
      await hydrate({ force: true });
      throw new Error("Unable to switch portal. Please retry.");
    }
    return next;
  };

  const value = {
    ...state,
    refresh: hydrate,
    switchPortal,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
